import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import UniversalElasticsearchSelect from '@client/components/ui/select/UniversalElasticsearchSelect';
import CultureTreeSelect from '@client/components/ui/select/CultureTreeSelect';

const CULTURE_FIELD = 'treatment_cultures.culture';
const CULTURE_ENDPOINT = '/api/cultures/tree?show_in_customer_filter_quick_processing=true';
const ANIMATION_CONFIG = {
  initial: false,
  animate: { height: 'auto', opacity: 1 },
  animateHidden: { height: 0, opacity: 0 },
  transition: { duration: 0.3 },
};

const StepContent = ({
  step,
  isActive,
  value,
  isValid,
  isRequired,
  isLastVisibleStep,
  onChange,
  onNext,
  onSkip,
  onForceRevalidation,
  selectionLabels,
}) => {
  const { t } = useTranslation('treatment-search');

  const hasSelections = value?.length > 0;
  const canProceed = isValid && !isLastVisibleStep;
  const isCultureStep = step.field === CULTURE_FIELD;

  const handleCultureChange = (selectedValues, selectedNodes) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    const nodes = Array.isArray(selectedNodes) ? selectedNodes : [];

    const labelObjects = nodes.filter(Boolean).map((node) => ({
      value: node.value || node.id,
      label: node.label || node.french_name || node.name || String(node.value || node.id),
      is_pfi: node.is_pfi || false,
      name_to_show_on_icon: node.name_to_show_on_icon || node.name_to_display_in_the_icon || null,
    }));

    onChange(step.key, values, labelObjects);

    if (step.autoProgress && values.length > 0 && !isLastVisibleStep) {
      setTimeout(() => onNext(step.key + 1), 100);
    }
  };

  const handleElasticsearchChange = (selectedValues, selectedItems) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [selectedValues].filter(Boolean);
    const labelObjects = Array.isArray(selectedItems) ? selectedItems : [selectedItems].filter(Boolean);

    onChange(step.key, values, labelObjects);

    if (step.autoProgress && values.length > 0 && !isLastVisibleStep) {
      setTimeout(() => onNext(step.key + 1), 100);
    }
  };

  const renderCultureSelect = () => (
    <CultureTreeSelect
      endpoint={CULTURE_ENDPOINT}
      labelField="french_name"
      isMulti={false}
      label={step.label ? t(step.label) : t(step.placeholder)}
      placeholder={t(step.placeholder)}
      value={value}
      onChange={handleCultureChange}
      checkStrictly
      languageFields={step?.languageConfig?.fields}
      languageLabels={step?.languageConfig?.labels}
      defaultLanguage={step?.languageConfig?.defaultLanguage || 'fr'}
      showLanguageSelector={!!step?.languageConfig}
      onLanguageChange={onForceRevalidation}
      style={{ width: '100%' }}
    />
  );

  const renderElasticsearchSelect = () => (
    <div className="w-full sm:w-1/2">
      <UniversalElasticsearchSelect
        placeholder={t(step.placeholder)}
        label={t(step.placeholder)}
        value={value}
        initialLabels={selectionLabels}
        onChange={handleElasticsearchChange}
        document={step.document}
        field={step.field}
        value_field={step.value_field}
        label_field={step.label_field}
        allowClear
        showSearch
        showSelectedTags={false}
        languageFields={step?.languageConfig?.fields}
        languageLabels={step?.languageConfig?.labels}
        defaultLanguage={step?.languageConfig?.defaultLanguage || 'fr'}
        showLanguageSelector={!!step?.languageConfig}
        mode="combobox"
      />
    </div>
  );

  const renderActionButtons = () => (
    <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
      {hasSelections && canProceed && (
        <button
          onClick={() => onNext(step.key + 1)}
          className="w-full sm:w-auto text-sm text-green-700 hover:text-green-800 font-medium py-2 sm:py-0 px-3 sm:px-0 rounded sm:rounded-none bg-green-50 sm:bg-transparent hover:bg-green-100 sm:hover:bg-transparent text-center order-1"
        >
          Continuer vers l'étape {step.key + 1} →
        </button>
      )}
      
      {!isRequired && canProceed && (
        <button
          onClick={() => onSkip(step.key)}
          className="w-full sm:w-auto text-sm text-gray-600 hover:text-gray-800 font-medium py-2 sm:py-0 px-3 sm:px-0 rounded sm:rounded-none bg-gray-50 sm:bg-transparent hover:bg-gray-100 sm:hover:bg-transparent text-center order-2 sm:ml-auto"
        >
          Passer cette étape →
        </button>
      )}
    </div>
  );

  return (
    <motion.div
      {...ANIMATION_CONFIG}
      animate={
        isActive ? ANIMATION_CONFIG.animate : ANIMATION_CONFIG.animateHidden
      }
      className="overflow-hidden"
    >
      <div
        className="p-4 sm:p-6 bg-[#f8fdec] rounded-b-[16px]"
        style={{ display: isActive ? 'block' : 'none' }}
      >
        <div>
          {step.description && (
            <p className="text-gray-600 mb-4">{t(step.description)}</p>
          )}
          {isCultureStep ? renderCultureSelect() : renderElasticsearchSelect()}
          {renderActionButtons()}
        </div>
      </div>
    </motion.div>
  );
};

export default StepContent;
